/**
 * `RawSheet` — the simplified, network-shaped wire format for a
 * sheet, plus the declarative helpers that hydrate it into a fully
 * typed `Sheet` with variant subclass instances.
 *
 * The hydration seam is intentionally split into small pure helpers
 * (`toDescriptor`, `toLocalId`, `emptyStores`) composed by
 * `hydrateSheet`, the implementor's builder (`@tento/lona/sheets/platform`:
 * the full `Sheet`). `RawSheet.hydrate` is the application's composition:
 * the same build, handed out as the sheet's reads face (`SheetModelReads`).
 *
 * Note on terminology: the verb "hydrate" here names the
 * **raw-sheet → typed-Sheet** materialization. It is distinct from
 * `AliasInstance.hydrated` (alias-detach state). Call sites resolve
 * the ambiguity via the `RawSheet.` namespace prefix.
 */

import type { RowWrites } from "../ledger/row-writes.ts";
import type { Option } from "../core/option.ts";
import { SheetRegistries } from "../sheet/sheet-registries.ts";
import type { RecomputeScheduler } from "../sheet/sheet-recompute-queue.ts";
import { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

import type { BuiltinAliasFactory } from "../aliases/alias-templates.ts";
import type { Descriptor } from "../rows/descriptor.ts";
import type { Backend } from "../backends/rows-backend.ts";
import { AliasTemplateOps } from "../aliases/alias-templates.ts";
import type { CellCardinality } from "../core/dtype.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { FormulaEngine } from "../formula/formula-engine.ts";
import {
  InvalidationSink,
  NewRowInserted,
  SheetStructureChanged,
} from "../tx/invalidation.ts";
import { EncodingRegistry } from "../core/encoding-registry.ts";
import { registerBuiltinEncodings } from "../encodings/builtin.ts";
import { registerBuiltinFacades } from "../indices/index.ts";
import { ParsedTree } from "./parse-tree.ts";
import { RowLocalId } from "../ids/row-local-id.ts";
import { Sheet, type SheetStores } from "../sheet/sheet.ts";
import { sheetInternals, sheetStores } from "../sheet/sheet-stores.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { sheetReadsOf, type SheetModelReads } from "../sheet/rows-sheet-view.ts";
import type { SyncPoll } from "../sync/sync-state.ts";
import { RowTypeSpecOps } from "../rows/row-type-spec.ts";
// Side-effect import: populates `ROW_TYPE_SPECS` with built-in
// entries (Phase 3d). Must land before any hydrate call reads
// the registry; raw-sheet.ts is the canonical hydrate entry
// point so we anchor the side effect here.
//
// Builtin alias templates (R6.2) used to be registered here too;
// they're now pure-static factories in `../aliases/builtin-alias-factories.ts`,
// composed by callers at `SheetModelCache` construction time.
import "../rows/row-type-specs-builtin.ts";
import { ComputedDataSource } from "../rows/variants/computed-data-source.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { PartialDateMemo } from "./partial-date-memo.ts";
import {
  AliasInstanceStore,
  RenderlessStore,
  RowMetadata,
  RowMetadataStore,
  SourceRowCellData,
  SyncStateStore,
  TreeStructure,
} from "../stores/index.ts";
import {
  hydrateDefaultRenderer,
  isSyntheticDefaultRendererId,
  shapeFromMeta,
} from "../rows/variants/hydrate-default-renderer.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { parseRow } from "../rows/variants/registry.ts";
import { isCanonicalKey } from "../backends/canonical-backend.ts";
import { LedgerBinding } from "../render/ledger-binding.ts";
import type { LedgerBumper } from "../stores/ledger-keys.ts";
import { LedgerShadow } from "../render/ledger-shadow.ts";
import { LedgerSynthetic } from "../render/ledger-synthetic.ts";
import { Timescale } from "../render/timescale.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { parseRawSheet, type RawCell, RawRow } from "../wire/raw-sheet-wire.ts";

import {
  cellCardinalityOf,
  descriptorBuilderTypes as codecDescriptorBuilderTypes,
  descriptorOf,
  reconcileRowMetadata,
  remapRowIds as codecRemapRowIds,
  rowLocalIdOf,
  type RowSnapshotSource,
  snapshotRow as codecSnapshotRow,
  unknownDescriptorTypes as codecUnknownDescriptorTypes,
} from "../sheet/sheet-row-codec.ts";

export type { RawCell } from "../wire/raw-sheet-wire.ts";
export { reconcileRowMetadata, wireTypeOf } from "../sheet/sheet-row-codec.ts";

export { RawRow } from "../wire/raw-sheet-wire.ts";

/** A server-emitted sheet as the hydrator reads it (declared with the wire types, `sheets/src/wire/raw-sheet-wire.ts`). */
export type RawSheet = import("../wire/raw-sheet-wire.ts").RawSheet;

/**
 * Types and values that go with `RawSheet`: `parse`, `toDescriptor`,
 * `unknownDescriptorTypes`, `descriptorBuilderTypes`, `toLocalId`,
 * `ShortFormatInput`, and 7 more.
 */
export namespace RawSheet {
  /**
   * `RawSheet` read at its JSON boundary (a `*.sheet.json` fixture, a stored
   * snapshot): every row through `RawRow.parse` (ids branded verbatim), the
   * sheet's own fields type-checked. The sheet `id` keeps its spelling, as a
   * `SheetNetworkKey` (the text every route takes). A mismatch is a
   * thrown `WireDecodeError` naming `RawSheet` and the path.
   */
  export function parse(raw: Json): RawSheet {
    return parseRawSheet(raw);
  }

  /**
   * Pure. Raw row → `Descriptor`, resolved through `registries` (a `data`
   * row is a chart alias when its lookup key has a data preset there).
   * Unknown wire types warn once and fall back to `text` so stale fixtures
   * or persisted rows do not abort hydrate.
   */
  /**
   * Decode the cell identity shape without constructing renderers or aliases.
   * Transport metadata can carry a source shape without authored alias props.
   * The caller supplies the existing semantic default for its row descriptor.
   */
  export function cellCardinality(row: RawRow, fallback: CellCardinality): CellCardinality {
    return cellCardinalityOf(row, fallback);
  }

  export function toDescriptor(row: RawRow, registries: SheetRegistries): Descriptor {
    return descriptorOf(row, registries);
  }

  /**
   * Return legacy wire row types that would hit the warn-fallback in
   * `toDescriptor`. This keeps fixture and import audits out of the
   * warning side effect, so tests can fail directly on missing
   * descriptor coverage.
   */
  export function unknownDescriptorTypes(sheet: RawSheet): readonly string[] {
    return codecUnknownDescriptorTypes(sheet);
  }

  /**
   * Every wire type with a registered descriptor builder. Lets parity
   * audits assert the reverse direction (no builder key is a qualified
   * renderer id outside the canonical catalog) without exposing the map.
   */
  export function descriptorBuilderTypes(): readonly string[] {
    return codecDescriptorBuilderTypes();
  }

  /**
   * Pure (given registry). Intern a `RowLocalId` for a raw row.
   * Repeated calls with the same registry + raw id return the same
   * instance (reference equality).
   */
  export function toLocalId(
    row: RawRow,
    registry: RowLocalId.Registry,
  ): RowLocalId {
    return rowLocalIdOf(row, registry);
  }

  /**
   * Short-format fixture input shape. Matches `RawFixture.sheet`
   * (an array of Unicode-tree lines) + an optional `id`/`title` for
   * the generated RawSheet.
   */
  export interface ShortFormatInput {
    readonly sheet: readonly string[];
    readonly id?: string;
    readonly title?: string;
  }

  /**
   * Convert a short-format fixture's `sheet` tree into a flat
   * `RawSheet`. Row ids are synthesized from tree position as
   * `r_<32-hex>` so alias anchors (which require a UUID) can resolve;
   * nested structure survives via `parentId`.
   *
   * Used by regression tests that feed every `.sheet[short].json`
   * through the hydrator to exercise `toDescriptor` + tree seeding.
   */
  export function fromShortFormat(input: ShortFormatInput): RawSheet {
    const tree = ParsedTree.parse(input.sheet);
    if (tree.kind !== "root") {
      throw new Error("fromShortFormat: expected root node");
    }
    const rows: RawRow[] = [];
    let index = 0;
    const visit = (node: ParsedTree, parentId: Option<string>): void => {
      if (node.kind === "root") {
        for (const c of node.children) visit(c, parentId);
        return;
      }
      if (node.kind !== "row") return;
      const myId = shortFormatRowId(index++);
      rows.push(toRawRow(node, myId, parentId));
      for (const c of node.children) visit(c, myId);
    };
    visit(tree, null);
    return {
      id: SheetNetworkKey.fromWire(input.id ?? "short-format-fixture"),
      ...(input.title !== undefined ? { title: input.title } : {}),
      rows,
    };
  }

  function shortFormatRowId(index: number): string {
    return `r_${index.toString(16).padStart(32, "0")}`;
  }

  /**
   * Fresh, empty `SheetStores` bound to the given sink. `binding` (row-identity
   * ledger, L2) threads the shared `LedgerBumper` + per-row `data:` mapper into
   * the row-keyed cell + metadata stores so their writes bump versions (LR1);
   * `null` for standalone / non-accessor hydrate (bumps become no-ops).
   * `shadow` (L3) is the per-sheet edge-mirror the recompute seams reach via
   * `stores.sourceCellData.ledgerShadow`; `null` when there is no ledger.
   * `synthetic` (L4) is the per-sheet synthetic-edge declarer reached via
   * `stores.sourceCellData.ledgerSynthetic`; `null` when there is no ledger.
   */
  export function emptyStores(
    sink: InvalidationSink,
    binding: Option<LedgerBinding>,
    shadow: Option<LedgerShadow>,
    synthetic: Option<LedgerSynthetic>,
    sheetId: SheetNetworkKey,
    registries: SheetRegistries,
  ): SheetStores {
    return {
      tree: new TreeStructure(sink, sheetId),
      sourceCellData: new SourceRowCellData(sink, binding, shadow, synthetic, registries.render),
      rowMetadata: new RowMetadataStore(sink, binding?.bumper ?? null),
      aliasInstance: new AliasInstanceStore(sink),
      syncState: new SyncStateStore(sink),
      renderless: new RenderlessStore(sink),
    };
  }

  /**
   * Hydrate a `RawSheet` and hand out the sheet as an application holds
   * one: its reads face (`SheetModelReads`, frozen; every row a frozen
   * `ModelRowReads`). The sheet is built exactly as `hydrateSheet` builds
   * it (the same side effects and the same thrown errors); no writer of it
   * is reachable from the face. A renderer or CLI implementor that drives
   * the sheet itself (adds rows, switches the timescale, subscribes to its
   * sink) builds it with `hydrateSheet` from `@tento/lona/sheets/platform`.
   */
  export function hydrate(
    raw: RawSheet,
    deps: RawSheet.HydrateDeps,
  ): SheetModelReads {
    return sheetReadsOf(hydrateSheet(raw, deps));
  }

  /**
   * What `hydrate` builds a sheet with. Every field is stated: `null` where
   * the sheet has none of it (a standalone or test sheet), a named mode
   * where a flag used to be. `HydrateDeps.standalone(backend)` states a
   * standalone sheet's; spread it and name what differs.
   */
  export interface HydrateDeps {
    readonly backend: Backend;
    /** The row-identity registry the sheet's row ids are interned in (its client's); `null`: a fresh one of its own. */
    readonly registry: Option<RowLocalId.Registry>;
    /** Where the sheet emits and hears invalidations (its client's sink); `null`: a fresh sink of its own. */
    readonly invalidationSink: Option<InvalidationSink>;
    /**
     * Client-scoped canonical cell-data store. Rows with `:~`-prefixed
     * lookup keys resolve their reads/writes here instead of the
     * sheet-local `sourceCellData`, so multiple sheets hosted by the
     * same Client share cells + dedup fetches. `null`: a fresh per-sheet
     * store (standalone / test).
     */
    readonly canonicalCells: Option<SourceRow.CellDataStore<string>>;
    /** Client-owned ordinary facts keyed by stable row identity; `null`: a standalone sheet store. Derived output is always sheet-local. */
    readonly ordinaryCells: Option<SourceRow.CellDataStore<RowLocalId>>;
    /** The per-sheet alias templates (`BuiltinAliasFactory`), registered before the rows are built. */
    readonly builtinAliases: readonly BuiltinAliasFactory[];
    /** How much of a computed-row recompute runs per tick; `null`: all of it, now (the immediate default). */
    readonly recomputeScheduler: Option<RecomputeScheduler>;
    /**
     * The lookup tables the sheet resolves its rows through (row-type
     * specs, computed sources, alias templates, data presets, the
     * render-skip override): its client's `ClientConfig.registries`, or
     * `SheetRegistries.BUILTINS` for a standalone sheet.
     */
    readonly registries: SheetRegistries;
    /**
     * The encoding decoders the sheet starts with: `"builtin"` (`event.v1`,
     * `task.v1`, `weather.v1`, `slot.v1`), or `"custom"` — none; the caller
     * ships its own set through `registerEncodings`.
     */
    readonly encodings: HydrateDeps.Encodings;
    /**
     * Registers additional decoders before inline `RawRow.cells` are
     * seeded: the seam for custom encodings whose wire cells may already
     * be present on the fetched `RawSheet`. `null`: none.
     */
    readonly registerEncodings: Option<(registry: EncodingRegistry) => void>;
    /**
     * Whether the sheet has a server-side record: `"server-backed"`, or
     * `"ephemeral"` (opened with a client-minted UUID and no record;
     * `Sheet.ephemeral`, so batch-prefetch paths route around the
     * sheet-scoped endpoint, which would 404).
     */
    readonly mode: HydrateDeps.Mode;
    /**
     * Sync-status poll capability, threaded to `Sheet.syncPoll` so
     * `ListDataSource` can poll a batch-fetch's in-flight background syncs
     * and re-fetch on completion. `null` in test / non-HTTP paths.
     */
    readonly syncPoll: Option<SyncPoll>;
    /**
     * Shared row-identity ledger bumper (row-identity ledger, L2), from the
     * accessor that owns the ledger: the sheet's cell + metadata store
     * writes bump `(key, facet)` versions (LR1). `null` in standalone /
     * test hydrate — bumps are no-ops.
     */
    readonly ledgerBumper: Option<LedgerBumper>;
    /**
     * The client's row-field ledger (`SheetModelCache.rowWrites`), shared by
     * every sheet of the client so a row held by two sheets has one pending
     * model. `null`: the sheet keeps its own (standalone / test).
     */
    readonly rowWrites: Option<RowWrites>;
  }

  export namespace HydrateDeps {
    /** Whether a hydrated sheet has a server-side record (`HydrateDeps.mode`). */
    export type Mode = "server-backed" | "ephemeral";
    /** The decoders a hydrated sheet starts with (`HydrateDeps.encodings`). */
    export type Encodings = "builtin" | "custom";

    /**
     * A standalone sheet's deps over `backend`: its own registry, sink and
     * canonical store, the built-in registries and encodings, no aliases,
     * server-backed, no sync poll, no ledger.
     */
    export function standalone(backend: Backend): HydrateDeps {
      return {
        backend,
        registry: null,
        invalidationSink: null,
        canonicalCells: null,
        ordinaryCells: null,
        builtinAliases: [],
        recomputeScheduler: null,
        registries: SheetRegistries.BUILTINS,
        encodings: "builtin",
        registerEncodings: null,
        mode: "server-backed",
        syncPoll: null,
        ledgerBumper: null,
        rowWrites: null,
      };
    }
  }

  /**
   * Inverse of `hydrate`: reconstruct a wire-format `RawSheet` from
   * the Sheet's current stores. Enables persistence round-trips —
   * hydrate → edit → serialize → hydrate yields the same state. Does
   * not emit its own computed cells for `FormulaDataSource`s (they derive
   * from the formula + upstream values at re-hydrate time).
   */
  export function serialize(sheet: Sheet): RawSheet {
    const source = rowSnapshotSourceOf(sheet);
    const rows: RawRow[] = [];
    for (const row of sheet.allRows()) {
      // Auto-hydrated default renderer children are re-synthesized
      // on re-hydrate from the source's `(dtype, cardinality)` via
      // `resolveDefaultRendererId` — their state (e.g. unit) lives on
      // the source's attribute bag. Keep them off the wire so the
      // raw sheet stays canonical: one raw row per user-facing row.
      if (isSyntheticDefaultRendererId(row.id) || AliasTemplateOps.isSyntheticChild(row)) {
        continue;
      }
      rows.push(codecSnapshotRow(source, row));
    }
    return {
      id: sheet.networkKey,
      ...(sheet.title != null ? { title: sheet.title } : {}),
      ...(sheet.width != null ? { width: sheet.width } : {}),
      rows,
    };
  }

  /**
   * Serialize a single row to its wire `RawRow` shape — the same
   * per-row projection `serialize` applies, exposed for callers that
   * snapshot part of the tree. Unlike `serialize`, this does NOT skip
   * synthetic rows; the caller decides what to snapshot. Used by
   * `Sheet.detachAlias` to materialize a forked alias's hydrated
   * children into real, server-backed rows.
   */
  export function snapshotRow(sheet: Sheet, row: ModelRow): RawRow {
    return codecSnapshotRow(rowSnapshotSourceOf(sheet), row);
  }

  /**
   * Rewrite a `RawRow`'s own id, its `parentId`, and the
   * `attributes.parentRowId` back-reference (carried by `storage:
   * "parent"` rows such as renderers) through `idRemap`. Ids outside
   * the remap pass through unchanged; every other field is preserved.
   * Shared by `Sheet.detachAlias` (virtual→real materialization) and
   * `SheetModelCache.rekeyRows` (ephemeral→server promote re-key).
   */
  export function remapRowIds(
    raw: RawRow,
    idRemap: ReadonlyMap<string, string>,
  ): RawRow {
    return codecRemapRowIds(raw, idRemap);
  }
}

/** What a row snapshot of `sheet` reads: its stores and its canonical cell store. */
function rowSnapshotSourceOf(sheet: Sheet): RowSnapshotSource {
  const stores = sheetStores(sheet);
  return {
    rowMetadata: stores.rowMetadata,
    tree: stores.tree,
    aliasInstance: stores.aliasInstance,
    renderless: stores.renderless,
    sourceCellData: stores.sourceCellData,
    canonicalCells: sheetInternals(sheet).canonicalCells,
  };
}

/**
 * Hydrate a `RawSheet` into the full, live `Sheet` (writers, stores and
 * sink included) with fully-instantiated
 * variant subclasses. The caller supplies a `Backend` and
 * (optionally) a `RowLocalId.Registry`; when omitted a fresh
 * registry is allocated.
 *
 * Side effects:
 *   - Populates `stores.tree` from each `RawRow.parentId` and/or
 *     parent-declared `RawRow.children`. The two are reconciled
 *     into a single child→parent map; if a row appears as a child
 *     in two different parents, or its `parentId` disagrees with
 *     a parent's `children` list, hydrate throws. Sibling order
 *     comes from the parent's `children` array when present,
 *     otherwise raw-row insertion order. A parent cycle (a row its
 *     own ancestor — two clients' concurrent cross-reparents, which
 *     neither client's local cycle guard sees) is cut at the edge
 *     that closes it, and a `parentId` naming no row of the sheet is
 *     dropped: that row becomes a sheet root, keeping its subtree,
 *     and hydrate traces `raw-sheet/parent-cycle-cut` or
 *     `raw-sheet/orphan-parent` (a warning; the sheet still opens
 *     with every row reachable).
 *   - Keeps the first of two raw rows with one id and drops the later
 *     one, tracing `raw-sheet/duplicate-row-id` (a warning; the server
 *     keys rows by id, so the sheet opens past a duplicate).
 *   Hydrate is the tolerant reader of wire data (a live open must still
 *   open); `RawSheet.parse`, the reader of offline sources (fixtures,
 *   dumps), refuses a duplicate id or a parent cycle instead.
 *   - Seeds `stores.aliasInstance` with each `AliasDescriptor`'s
 *     template + props so alias row consumers read non-empty
 *     instances out of the gate.
 *
 * The implementor's builder (`@tento/lona/sheets/platform`): an application
 * hydrates through `RawSheet.hydrate`, which answers the sheet's reads face.
 */
export function hydrateSheet(raw: RawSheet, deps: RawSheet.HydrateDeps): Sheet {
  const registry = deps.registry ?? new RowLocalId.Registry();
  const invalidationSink = deps.invalidationSink ?? InvalidationSink.create();
  const binding = deps.ledgerBumper != null
    ? LedgerBinding.forSheet(deps.ledgerBumper)
    : null;
  // The L3 shadow mirrors the old formula/derived graph onto the ledger
  // (dark). Built only when a ledger exists; shares the accessor's ledger via
  // the bumper. Threaded into the stores like `binding` so the recompute
  // seams reach it through `stores.sourceCellData.ledgerShadow`.
  const shadow = binding != null
    ? LedgerShadow.make(binding.bumper.ledger)
    : null;
  // The L4 synthetic declarer (dark): declares the geometry-pref / reexpand /
  // coverage edges the old graph does NOT carry. Built only when a ledger
  // exists; shares the accessor's ledger via the bumper. Reached by the
  // reexpand seam through `stores.sourceCellData.ledgerSynthetic`.
  const synthetic = binding != null
    ? LedgerSynthetic.make(binding.bumper.ledger)
    : null;
  const registries = deps.registries;
  const empty = RawSheet.emptyStores(invalidationSink, binding, shadow, synthetic, raw.id, registries);
  const stores = deps.ordinaryCells == null ? empty : {
    ...empty,
    sourceCellData: SourceRowCellData.withOrdinary(invalidationSink, binding, shadow, synthetic, registries.render, deps.ordinaryCells),
  };

  // Pre-compute (id, descriptor, rawType) in raw order, carrying the raw
  // row alongside so each pass below reads it without a parallel index.
  // rawType is needed for FormulaDataSource dispatch (formulas emit any
  // dtype, so the descriptor alone can't distinguish them).
  const prepared = keepFirstRowPerId(raw.id, raw.rows.map((r): PreparedRow => ({
    id: rowLocalIdOf(r, registry),
    descriptor: descriptorOf(r, registries),
    rawType: r.type,
    rawRow: r,
  })));

  // Parent-id wiring for auto-hydrated renderer children. Recorded
  // alongside row construction so the tree-insert pass below can
  // place them under their source without a second resolution step.
  const syntheticRendererParentOf = new Map<RowLocalId, RowLocalId>();

  const sheet = Sheet.withRows(
    {
      id: raw.id,
      ...(raw.title !== undefined ? { title: raw.title } : {}),
      ...(raw.width !== undefined ? { width: raw.width } : {}),
      timescale: decodedTimescale(raw),
      ...(raw.accessRole !== undefined ? { accessRole: raw.accessRole } : {}),
      backend: deps.backend,
      stores,
      registry,
      invalidationSink,
      ...(deps.canonicalCells != null ? { canonicalCells: deps.canonicalCells } : {}),
      builtinAliases: deps.builtinAliases,
      ...(deps.recomputeScheduler != null ? { recomputeScheduler: deps.recomputeScheduler } : {}),
      ephemeral: deps.mode === "ephemeral",
      ...(deps.syncPoll != null ? { syncPoll: deps.syncPoll } : {}),
      registries,
      rowWrites: deps.rowWrites,
    },
    (sheet): readonly ModelRow[] => {
      const rows: ModelRow[] = [];
      // Wire type by raw id — lets the synthesis loop see a row's parent
      // type before the tree is seeded (a later pass).
      const rawTypeById = new Map(raw.rows.map((r) => [r.id, r.type]));
      for (const { id, descriptor, rawType, rawRow } of prepared) {
        const row = parseRow(id, descriptor, sheet, rawType);
        rows.push(row);
        // For every source row, the per-slot default-renderer lookup
        // decides whether to hydrate a child renderer beneath it.
        // Obj dtypes, unsupported slots, and non-user parent id
        // variants return `null` and skip synthesis (see
        // `hydrate-default-renderer.ts` for scope). Skip if the raw
        // row already declares this renderer child itself (e.g. a
        // serialize → re-hydrate round-trip) — we detect that by the
        // synthetic id already being present in `prepared`.
        if (!(row instanceof SourceRow)) continue;
        // Copy-on-write: a render-less source has had its default renderer
        // MATERIALIZED into a real renderer child (persisted in the raw, so
        // already parsed above and present in `prepared`). Seed the flag
        // from the wire and skip synthesizing a virtual one. Inert until the
        // materialize-on-edit path sets `renderless` — no row carries it yet.
        if (rawRow.attributes?.renderless === true) {
          sheetStores(sheet).renderless.init(id);
          continue;
        }
        // Aggregate constituents don't synthesize their own renderer — the
        // aggregate's renderers hang off the `list` parent (see
        // `ListDataSource`). The tree isn't seeded yet in this pass, so
        // detect the parent via the raw wire type (`list` ⇒ ListDataSource);
        // `hydrateDefaultRenderer`'s `source.parent` guard covers the
        // tree-built `addRow` path.
        const parentId = rawRow.parentId;
        if (parentId != null && rawTypeById.get(parentId) === "list") {
          continue;
        }
        // Shape from the *reconciled* metadata (wire `meta` merged with the
        // row-type spec's `defaultMetadata`, e.g. logs/v1) — `rowMetadata`
        // isn't seeded until a later pass, so reconcile here to match what
        // the `addRow` path reads from the seeded store.
        const child = hydrateDefaultRenderer(
          row,
          sheet,
          shapeFromMeta(reconcileRowMetadata(rawRow, sheet.registries)),
        );
        if (child == null) continue;
        const alreadyDeclared = prepared.some(({ id: pid }) => pid === child.id);
        if (alreadyDeclared) continue;
        rows.push(child);
        syntheticRendererParentOf.set(child.id, id);
      }
      return rows;
    },
    (sheet) => {
      registerBuiltinFacades(sheet);
      if (deps.encodings === "builtin") registerBuiltinEncodings(sheet.encodings);
      deps.registerEncodings?.(sheet.encodings);
    },
  );

  // Late-bind the ledger's address resolver to the live sheet (L2): before
  // this the binding used only what a `RowLocalId` affords; now
  // `resolveAddress` delegates to `AddressOf` via the live row (frozen
  // `Sheet.peekRow` — a public read, no edit).
  if (binding != null) {
    const lookup = (id: RowLocalId): Option<ModelRow> =>
      sheet.peekRow(id) ?? null;
    binding.attachRowLookup(lookup);
    // Same live-row resolver for the L3 shadow's producer translation.
    shadow?.attachRowLookup(lookup);
  }

  // Declared BEFORE the tree is seeded: an insert under a computed parent
  // recomputes it at once (the sheet's structure listener), and on a client
  // that already holds its inputs' facts (a reopened sheet) that recompute
  // writes cells. Undeclared, the first write creates the row's store at the
  // `single` default and the `multi` declaration then throws.
  //
  // Declare per-row cell cardinality so `SourceRowCellData` routes
  // into the right `RowCellData` (sorted index for `single`,
  // cell-id map for `multi`). Only source rows carry cells.
  //
  // The client-scoped `canonicalCells` store is declared here too, and
  // deliberately NOT from the inline-cell seeding loop below: that loop
  // skips any row arriving without cells, and a canonical row with no
  // inline cells (`:~…:calendar:{calId}:events` — events are fetched by
  // range afterwards) still needs its cardinality on record before the
  // first write lazily creates the store at the `"single"` default. See
  // `Sheet.declareCanonicalCardinality` for what that default cost.
  for (const { id, descriptor, rawRow } of prepared) {
    if (descriptor.role !== "source") continue;
    const actualSource = sheet.peekRow(id);
    if (!(actualSource instanceof SourceRow)) throw new Error("RawSheet.hydrate: source descriptor has no source variant");
    stores.sourceCellData.declareCellOwnership(id, actualSource.cellsDerived ? "derived" : "stored");
    stores.sourceCellData.setCardinality(id, descriptor.cellCardinality);
    const attrLookupKey = rawRow.attributes?.lookupKey;
    const lookupKey = rawRow.lookupKey ??
      (typeof attrLookupKey === "string" ? attrLookupKey : undefined);
    const source = sheet.peekRow(id);
    if (isCanonicalKey(lookupKey) && !(source instanceof SourceRow && source.cellsDerived)) {
      sheetInternals(sheet).canonicalCells.setCardinality(
        lookupKey,
        descriptor.cellCardinality,
      );
    }
  }

  // Seed tree from `parentId` and parent-declared `children`. The
  // two forms are dual representations of the same edge; we
  // reconcile into one child→parent map keyed by `RowLocalId`. If a
  // row appears with disagreeing parents, throw — the wire shape is
  // inconsistent and any silent pick would mask data corruption.
  //
  // Sibling order: when a parent declares `children: [a, b, c]`,
  // those siblings hydrate in array order. Rows whose parent edge
  // comes from `parentId` (or who are sheet roots) keep raw-row
  // insertion order.
  const childToParent = new Map<RowLocalId, RowLocalId>();
  const explicitOrder = new Map<RowLocalId, number>();
  for (const { id: parentLocalId, rawRow } of prepared) {
    if (!rawRow.children || rawRow.children.length === 0) continue;
    for (const [k, childRaw] of rawRow.children.entries()) {
      const childLocalId = registry.resolveFromRawId(childRaw);
      const existing = childToParent.get(childLocalId);
      if (existing != null && existing !== parentLocalId) {
        throw new Error(
          `RawSheet.hydrate: row "${childRaw}" claimed as child by ` +
            `multiple parents`,
        );
      }
      childToParent.set(childLocalId, parentLocalId);
      explicitOrder.set(childLocalId, k);
    }
  }
  for (const { id, rawRow } of prepared) {
    if (rawRow.parentId == null) continue;
    const parentLocalId = registry.resolveFromRawId(rawRow.parentId);
    const fromChildren = childToParent.get(id);
    if (fromChildren != null && fromChildren !== parentLocalId) {
      throw new Error(
        `RawSheet.hydrate: row "${rawRow.id}" parentId disagrees ` +
          `with its parent's children list`,
      );
    }
    childToParent.set(id, parentLocalId);
  }
  detachUnreachableParents(raw.id, prepared, childToParent);

  // Per-parent insertion counters: rows declared via `children`
  // claim slots in array order; remaining rows tack on after.
  const positionOf = new Map<Option<RowLocalId>, number>();
  const orderedFor = new Map<RowLocalId, RowLocalId[]>();
  const unorderedFor = new Map<Option<RowLocalId>, RowLocalId[]>();
  for (const { id } of prepared) {
    const parent = childToParent.get(id) ?? null;
    const k = explicitOrder.get(id);
    if (parent != null && k != null) {
      let bucket = orderedFor.get(parent);
      if (!bucket) {
        bucket = [];
        orderedFor.set(parent, bucket);
      }
      bucket[k] = id;
    } else {
      let bucket = unorderedFor.get(parent);
      if (!bucket) {
        bucket = [];
        unorderedFor.set(parent, bucket);
      }
      bucket.push(id);
    }
  }
  const insertUnder = (parent: Option<RowLocalId>): void => {
    const ordered = parent != null ? orderedFor.get(parent) : undefined;
    if (ordered) {
      for (const cid of ordered) {
        if (cid == null) continue;
        const pos = positionOf.get(parent) ?? 0;
        positionOf.set(parent, pos + 1);
        stores.tree.insert(cid, parent, pos);
      }
    }
    const unordered = unorderedFor.get(parent);
    if (unordered) {
      for (const cid of unordered) {
        const pos = positionOf.get(parent) ?? 0;
        positionOf.set(parent, pos + 1);
        stores.tree.insert(cid, parent, pos);
      }
    }
  };
  // The tree is built insert by insert; the computed-row pass below covers
  // every computed parent once over the finished tree, so no insert recomputes
  // its parent on the way (released right before that pass).
  const internals = sheetInternals(sheet);
  const releaseInsertRecomputes = internals.holdInsertRecomputes();
  insertUnder(null);
  // Recurse via the prepared list — every non-root row lands once
  // its parent has been inserted (parents come from earlier raw
  // rows in well-formed input; if not, `stores.tree.insert` fails
  // loudly which is the right signal).
  for (const { id } of prepared) {
    insertUnder(id);
  }

  // Insert auto-hydrated renderer children beneath their source as
  // position 0 (first child). Synthetic children don't appear in the
  // raw wire format, so they come after the raw-row insertion pass.
  // If a source already has raw children at this point, the
  // renderer takes slot 0 and everything else shifts down by one —
  // matching alias-style hydration where the hydrated child is
  // conceptually part of the source's identity, not user content.
  for (const [childId, parentId] of syntheticRendererParentOf) {
    stores.tree.insert(childId, parentId, 0);
  }

  // Seed inline cells from the wire format. Uses `init` (not
  // `set`) so subscribers attached before hydrate don't see
  // N spurious `ColumnDataChanged` events for pre-existing data.
  // Rows share their dates (one string per day): each distinct one is parsed once.
  const dates = new PartialDateMemo(PartialDateMemo.CAPACITY);
  stores.sourceCellData.batch(() => {
  for (const { id, descriptor, rawRow } of prepared) {
    const rawCells = rawRow.cells;
    if (!rawCells || rawCells.length === 0) continue;
    const encoding = encodingFromMeta(rawRow.meta ?? null);
    const attrLookupKey = rawRow.attributes?.lookupKey;
    const lookupKey = rawRow.lookupKey ??
      (typeof attrLookupKey === "string" ? attrLookupKey : undefined);
    const source = sheet.peekRow(id);
    const canonicalKey = isCanonicalKey(lookupKey) && !(source instanceof SourceRow && source.cellsDerived) ? lookupKey : null;
    // Source rows always seed. An alias row seeds ONLY when it carries
    // canonical inline cells (a `data` alias whose cells belong to its
    // expanded source child sharing the key) — those cells land in the
    // key-addressed `canonicalCells` store, never the row-id-addressed
    // `sourceCellData`. Before this guard existed the alias case was
    // silently dropped, so a preset-matched `data` row (whose descriptor
    // becomes role `alias` the moment its lookupKey resolves in
    // DATA_PRESETS) lost every inline cell at hydrate and its chart drew
    // nothing — while the comment below already claimed the alias case
    // was handled.
    if (
      descriptor.role !== "source" &&
      !(descriptor.role === "alias" && canonicalKey !== null)
    ) {
      continue;
    }
    // Kept alongside the unconditional declaration above rather than folded
    // into it: that loop covers `role === "source"` rows only, while this one
    // also reaches an alias row carrying canonical inline cells (a `data`
    // alias whose cells belong to its expanded source child sharing the key).
    // Re-declaring the same value is a no-op. The id-keyed `sourceCellData`
    // path still needs a real source row, so a non-canonical inline cell on
    // a non-source row has no home: skip it.
    //
    // An alias descriptor carries no cellCardinality of its own - its cells
    // belong to the expanded source child, and "single" is the default that
    // store applies (one cell per date), which is the only shape inline
    // `data` seeds take.
    if (canonicalKey === null && descriptor.role !== "source") continue;
    const cellCardinality = descriptor.role === "source"
      ? descriptor.cellCardinality
      : "single";
    if (canonicalKey !== null) {
      internals.canonicalCells.setCardinality(canonicalKey, cellCardinality);
    }
    // The one read rule (`EncodingRegistry.decodeRead`): a wire null stays a
    // null cell, a rejected value is left out, counted and traced once per row.
    const decoder = sheet.encodings.readOf(encoding, canonicalKey ?? rawRow.id.toString());
    for (const cell of rawCells) {
      const parsed = dates.parse(cell.date);
      if (!parsed.isOk) {
        throw new Error(
          `RawSheet.hydrate: invalid cell date "${cell.date}" on row ` +
            `${rawRow.id}; expected NaiveDate.Partial format ` +
            `(e.g. "ymd-2026-4-18").`,
        );
      }
      const decoded = decoder.decode(cell.value, cell.cellId ?? cell.date);
      if (decoded === EncodingRegistry.REJECTED) continue;
      const parsedDate = parsed.exp();
      if (parsedDate == null) {
        throw new Error(
          `RawSheet.hydrate: parsed cell date "${cell.date}" produced no value on row ` +
            `${rawRow.id}.`,
        );
      }
      if (canonicalKey !== null) {
        sheetInternals(sheet).canonicalCells.init(
          canonicalKey,
          parsedDate,
          decoded,
          cell.cellId ?? null,
          null,
        );
      } else {
        stores.sourceCellData.init(
          id,
          parsedDate,
          decoded,
          cell.cellId ?? null,
          null,
        );
      }
      sheetInternals(sheet).trackPromotionFlushCell(id, parsedDate, cell.cellId ?? null);
    }
    decoder.end();
  }

  });

  // Seed alias instances for alias rows. Template is descriptor
  // shape; props/overrides/hydrated seed from the raw row's
  // `attributes` bag (wire format) since they're per-row mutable
  // state.
  for (const { id, descriptor, rawRow } of prepared) {
    if (descriptor.role !== "alias") continue;
    const a = rawRow.attributes ?? {};
    stores.aliasInstance.init(id, {
      template: descriptor.template,
      props: AliasTemplateOps.resolveInitialProps(descriptor.template, a, rawRow, sheet),
      overrides: Json.child(a, "overrides") ?? {},
      hydrated: Boolean(a.hydrated),
    });
  }

  // Seed per-row metadata so `row.label` / `row.attributes()` /
  // `FormulaDataSource.expression` / row.lookupKey read raw values
  // without explicit setter calls. `init` does NOT emit — seed
  // events would confuse subscribers that attach before hydrate.
  for (const { id, rawRow } of prepared) {
    // Merge static RowTypeSpec defaults (from `ROW_TYPE_SPECS`)
    // with the row's wire attributes. Wire instance wins at
    // the top level; `children` is deep-merged. When no spec is
    // registered this falls back to the raw attributes unchanged.
    const reconciledAttrs = RowTypeSpecOps.reconcileForWireType(
      rawRow.type,
      rawRow,
      rawRow.attributes ?? null,
      registries,
    );
    const meta = reconcileRowMetadata(rawRow, registries);
    const metadata = RowMetadata.of({
      ...(rawRow.label !== undefined ? { label: rawRow.label } : {}),
      ...(rawRow.lookupKey !== undefined
        ? { lookupKey: rawRow.lookupKey }
        : {}),
      ...(rawRow.formula !== undefined ? { formula: rawRow.formula } : {}),
      ...(rawRow.heightPx !== undefined ? { heightPx: rawRow.heightPx } : {}),
      ...(reconciledAttrs ? { attributes: reconciledAttrs } : {}),
      ...(Object.keys(meta).length > 0 ? { meta } : {}),
    });
    stores.rowMetadata.init(id, metadata);
  }

  // Expand declarative childSpecs into virtual child rows: the
  // type's `RowTypeSpec.childSpecs`, else the instance's JSON
  // `attributes.childSpecs` (`RowTypeSpecOps.childSpecsFor`). Each
  // spec becomes a virtual child — virtual id =
  // `RowKey.virtual(parentHex, preset)`, descriptor derived from the
  // spec's type. Runs after the main insertion so parents exist when
  // we resolve their ids.
  for (const { id: parentId, rawRow } of prepared) {
    // Only rows declaring a real wire ID (not virtual /
    // system) can own child specs — virtual parents would
    // produce unstable grandchild keys.
    if (!rawRow.id.startsWith("r_")) continue;
    const parentHex = rawRow.id.slice(2);
    AliasTemplateOps.expandChildSpecsRecursive(
      sheet,
      parentId,
      parentHex,
      RowTypeSpecOps.childSpecsFor(rawRow.type, rawRow, registries),
      stores.rowMetadata.get(parentId)?.attributes ?? null,
    );
  }

  for (const row of sheet.allRows()) {
    if (!(row instanceof AliasRow)) continue;
    AliasTemplateOps.expandInstance(row, sheet);
  }

  releaseInsertRecomputes();
  const computedRange = FormulaEngine.observedRange(sheet);
  if (computedRange) {
    for (const row of sheet.allRows()) {
      if (!(row instanceof ComputedDataSource)) continue;
      row.recompute(computedRange);
    }
  }

  // Build the formula dependency graph + fill initial derived
  // values. The observed range spans the union of every seeded
  // cell's date; if no cells were seeded, `recomputeFormulas`
  // becomes a no-op.
  FormulaEngine.buildDependencyGraph(sheet);
  const observed = FormulaEngine.observedRange(sheet);
  if (observed) FormulaEngine.recomputeFormulas(sheet, observed);

  // L3 (dark): mirror the just-built formula edge set onto the ledger. Only
  // consumers with edges are declared; derived rows re-declare on their next
  // recompute (their edges were wiped by `buildDependencyGraph`).
  shadow?.syncAllFromGraph(sheetInternals(sheet).graph, sheet.allRows());

  // L4 (dark): declare the synthetic edges the old graph does NOT carry —
  // render-geometry-pref, alias reexpand, and per-row coverage. Runs the LI3
  // pref-exhaustiveness check + flushes the `ledger/synthetic` breadcrumb. Declare-lag:
  // rows added later via the frozen `Sheet.addRow` path re-declare only at the
  // next `syncAll`; re-expansion re-declares editably via `wireDeclaredReexpand`
  // → `ledgerSynthetic.onReexpand`.
  synthetic?.syncAll(sheet);

  // L5 promotion (dark for the DOM, live for the CLI): wire the render-cursor
  // registry's SDK-owned eviction. This is the one non-frozen seam that has the
  // sink, the live sheet, and the freshly-minted cursor handle all in scope,
  // and it runs AFTER hydrate's own insert bursts — those fire `NewRowInserted`
  // before we subscribe, so hydrate never self-bumps the epoch.
  //   • `SheetStructureChanged` (reparent / delete / reorder — id-less + coalesced)
  //     ⇒ reconcile records against the live row set + bump the structural epoch.
  //   • `NewRowInserted` (carries an id, O(1)) ⇒ epoch-only bump so siblings whose
  //     child-set/position output shifts re-paint once.
  // BOTH events are sheet-scoped (`concerns`): this registry is minted per
  // sheet, but the sink is shared app-wide, so an unfiltered insert in sheet A
  // used to bump sheet B's epoch and force B to re-paint every row it had
  // already painted (sdk-0 C8, the cross-sheet epoch storm). An unscoped event
  // (`sheetId == null`) still matches every sheet — the conservative fallback.
  // Ledger-less sheets mint no registry, so this whole block is skipped.
  const renderCursors = stores.sourceCellData.renderCursors;
  if (renderCursors != null) {
    // Released with the sheet (`Sheet.dispose`): the sink is the client's,
    // and a disposed sheet it still held would stay reachable from it.
    sheetInternals(sheet).addDisposer(invalidationSink.subscribe((event) => {
      if (event instanceof SheetStructureChanged) {
        if (!event.concerns(sheet.id)) return;
        renderCursors.reconcile(sheet.allRows().map((row) => row.id));
      } else if (event instanceof NewRowInserted) {
        if (!event.concerns(sheet.networkKey)) return;
        renderCursors.invalidateStructure();
      }
    }));
  }

  return sheet;
}

/**
 * `prepared` with one row per local id (`hydrateSheet`): the first row
 * listed under an id is kept, each later one dropped and traced
 * (`raw-sheet/duplicate-row-id`, a warning). Deterministic, and the kept row
 * is the one the registry already maps the id to; the server keys rows by
 * id, so a duplicate on the wire is a server fault the sheet still opens
 * past. `RawSheet.parse` (a fixture, a dump) refuses one instead.
 */
function keepFirstRowPerId(sheetId: SheetNetworkKey, prepared: readonly PreparedRow[]): readonly PreparedRow[] {
  const firstAt = new Map<RowLocalId, number>();
  const kept: PreparedRow[] = [];
  for (const [i, row] of prepared.entries()) {
    const first = firstAt.get(row.id);
    if (first !== undefined) {
      Tracing.warn("net")("raw-sheet/duplicate-row-id", { sheetId, rowId: row.rawRow.id, dropped: i, kept: first });
      continue;
    }
    firstAt.set(row.id, i);
    kept.push(row);
  }
  return kept;
}

/**
 * Make every prepared row reachable from the sheet's roots before the tree
 * is seeded (`hydrateSheet`), editing `childToParent` in place:
 * - a parent that is no row of the sheet (`parentId` naming a missing row)
 *   is dropped — the row becomes a root (`raw-sheet/orphan-parent`);
 * - a parent cycle is cut at the edge that closes it, walking up from each
 *   row in raw order: the row whose parent is already on the walk becomes
 *   a root, keeping its subtree (`raw-sheet/parent-cycle-cut`).
 * Each repair is traced as a warning; the walk visits every row once, so a
 * corrupt payload costs O(rows), never an unbounded loop.
 */
function detachUnreachableParents(
  sheetId: SheetNetworkKey,
  prepared: readonly PreparedRow[],
  childToParent: Map<RowLocalId, RowLocalId>,
): void {
  const rawIdOf = new Map<RowLocalId, RowNetworkKey>();
  for (const { id, rawRow } of prepared) rawIdOf.set(id, rawRow.id);
  for (const [child, parent] of childToParent) {
    if (rawIdOf.has(parent)) continue;
    childToParent.delete(child);
    Tracing.warn("net")("raw-sheet/orphan-parent", {
      sheetId,
      rowId: rawIdOf.get(child) ?? String(child),
      parentId: String(parent),
    });
  }
  const settled = new Set<RowLocalId>();
  for (const { id: start } of prepared) {
    const walk = new Set<RowLocalId>();
    let child: Option<RowLocalId> = null;
    let cursor: Option<RowLocalId> = start;
    while (cursor != null && !settled.has(cursor)) {
      if (walk.has(cursor)) {
        // `child`'s edge to `cursor` closes the cycle (`child` is set: the
        // walk already holds `cursor`).
        const cut = child;
        if (cut != null) {
          childToParent.delete(cut);
          Tracing.warn("net")("raw-sheet/parent-cycle-cut", {
            sheetId,
            rowId: rawIdOf.get(cut) ?? String(cut),
            parentId: rawIdOf.get(cursor) ?? String(cursor),
          });
        }
        break;
      }
      walk.add(cursor);
      child = cursor;
      cursor = childToParent.get(cursor) ?? null;
    }
    for (const id of walk) settled.add(id);
  }
}

/**
 * The sheet's wire `timeScale` as a `Timescale`: absent is `null` (the sheet
 * opens at `"day"`), and so is a spelling `Timescale.parse` refuses
 * (`"custom"`, a typo) — reported as a decode issue
 * (`raw-sheet/time-scale-decode`), never silently read as `"day"`.
 */
function decodedTimescale(raw: RawSheet): Option<Timescale> {
  if (raw.timeScale == null) return null;
  const timeScale = Timescale.parse(raw.timeScale);
  if (timeScale == null) {
    Tracing.log("raw-sheet/time-scale-decode", { sheetId: raw.id, timeScale: raw.timeScale });
  }
  return timeScale;
}

/**
 * One raw row resolved for hydrate: its local id, descriptor, wire type,
 * and the raw row itself, so every hydrate pass iterates one list instead
 * of indexing `raw.rows` in parallel.
 */
interface PreparedRow {
  readonly id: RowLocalId;
  readonly descriptor: Descriptor;
  readonly rawType: string;
  readonly rawRow: RawRow;
}

function encodingFromMeta(
  meta: Option<JsonObject>,
): Option<string> {
  const encoding = meta?.encoding;
  return typeof encoding === "string" ? encoding : null;
}

// ─── Short-format row translation ──────────────────────────────────

interface ParsedRowNode {
  readonly kind: "row";
  readonly type: string;
  readonly attrs: Record<string, string>;
  readonly label: Option<string>;
}

function toRawRow(
  node: ParsedRowNode,
  id: string,
  parentId: Option<string>,
): RawRow {
  const {
    label: labelAttr,
    formula,
    lookupKey,
    timeScale,
    ...rest
  } = node.attrs;
  return {
    id: RowNetworkKey.fromWire(id),
    type: node.type,
    label: node.label ?? labelAttr,
    formula,
    lookupKey,
    timeScale,
    ...(parentId != null ? { parentId: RowNetworkKey.fromWire(parentId) } : {}),
    ...(Object.keys(rest).length > 0 ? { attributes: rest } : {}),
  };
}
